import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import Sidebar from "@/components/studio/Sidebar";
import Editor from "@/components/studio/Editor";
import BookBible from "@/components/studio/BookBible";
import CoverModal from "@/components/studio/CoverModal";
import ExportModal from "@/components/studio/ExportModal";
import SettingsModal from "@/components/studio/SettingsModal";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Studio() {
  const { projectId } = useParams();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState("editor");
  const [loading, setLoading] = useState(true);
  const [modals, setModals] = useState({ cover: false, export: false, settings: false });

  const setModal = (k, v) => setModals((m) => ({ ...m, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/chapters`),
      ]);
      setProject(p.data);
      setChapters(c.data);
      setActiveId((prev) => prev || c.data[0]?.id || null);
    } catch {
      toast.error(t("toast.error"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const addChapter = async () => {
    const { data } = await api.post(`/projects/${projectId}/chapters`, { title: `${t("studio.chapter")} ${chapters.length + 1}`, content: "" });
    setChapters((prev) => [...prev, data]);
    setActiveId(data.id);
    setView("editor");
  };

  const deleteChapter = async (id) => {
    await api.delete(`/chapters/${id}`);
    setChapters((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
  };

  const onChapterUpdated = useCallback((updated) => {
    setChapters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const selectChapter = (id) => { setActiveId(id); setView("editor"); };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-900"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }

  const activeChapter = chapters.find((c) => c.id === activeId);

  return (
    <div className="flex bg-ink-900 grain min-h-screen">
      <Sidebar
        project={project}
        chapters={chapters}
        activeChapterId={activeId}
        view={view}
        onSelectChapter={selectChapter}
        onAddChapter={addChapter}
        onDeleteChapter={deleteChapter}
        onSetView={setView}
        onOpenCover={() => setModal("cover", true)}
        onOpenExport={() => setModal("export", true)}
        onOpenSettings={() => setModal("settings", true)}
      />

      <main className="flex-1 min-w-0">
        {view === "editor" && activeChapter ? (
          <Editor key={activeChapter.id} chapter={activeChapter} project={project} onChapterUpdated={onChapterUpdated} />
        ) : view === "bible" ? (
          <BookBible projectId={projectId} />
        ) : (
          <div className="h-screen flex items-center justify-center text-slate-500">{t("studio.addChapter")}</div>
        )}
      </main>

      {project && (
        <>
          <CoverModal open={modals.cover} onOpenChange={(v) => setModal("cover", v)} project={project} onUpdated={(data) => setProject(data)} />
          <ExportModal open={modals.export} onOpenChange={(v) => setModal("export", v)} project={project} />
          <SettingsModal open={modals.settings} onOpenChange={(v) => setModal("settings", v)} project={project} onUpdated={(data) => setProject(data)} />
        </>
      )}
    </div>
  );
}
